import { faCheck, faClose } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useTranslation } from "react-i18next";
import { styled } from "styled-components";

import type { Question } from "api/services/content/schemas/lessons";
import { Body, Caption, H4, Title } from "shared/atoms/PatientApp";

import { QuestionTip } from "./QuestionTip";

interface Props {
  index: number;
  question: Question;
  total: number;
}

export const QuizScreen: React.FC<Props> = ({ index, question, total }) => {
  const { t } = useTranslation();

  return (
    <>
      <Title>{t("common.quiz")}</Title>
      <QuizContent>
        <StyledCaption>{t("library.question_part", { index: index + 1, total })}</StyledCaption>
        <H4>{question.title}</H4>
        <AnswersContainer>
          {question.answers.map((answer, i) => {
            const key = `answer-${i}`;

            return (
              <Answer key={key} $isCorrect={answer.correct}>
                <AnswerText>
                  <Body>{answer.text}</Body>
                </AnswerText>
                <RadioContainer $isCorrect={answer.correct}>
                  <FontAwesomeIcon icon={answer.correct ? faCheck : faClose} fontSize={24} color="#fff" />
                </RadioContainer>
              </Answer>
            );
          })}
        </AnswersContainer>
        {question.tip_description && <QuestionTip tip_description={question.tip_description} />}
      </QuizContent>
    </>
  );
};

const QuizContent = styled.div`
  padding: 32px 16px 24px;
`;

const StyledCaption = styled(Caption)`
  margin-bottom: 8px;
  color: ${({ theme }) => theme.colors.redesign.db60};
`;

const AnswersContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin: 16px 0;
`;

const Answer = styled.div<{ $isCorrect?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px;
  background: ${({ theme, $isCorrect }) => ($isCorrect ? theme.colors.redesign.g10 : theme.colors.redesign.r10)};
  border: 1px solid ${({ theme, $isCorrect }) => ($isCorrect ? theme.colors.redesign.g100 : theme.colors.redesign.r100)};
  border-radius: 12px;
  overflow: hidden;
`;

const AnswerText = styled.div`
  flex-shrink: 1;
  margin-right: 8px;
`;

const RadioContainer = styled.div<{ $isCorrect?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 40px;
  min-width: 40px;
  max-height: 40px;
  max-width: 40px;
  background: ${({ theme, $isCorrect }) => ($isCorrect ? theme.colors.redesign.g100 : theme.colors.redesign.r100)};
  border-radius: 50%;
`;
